import { html, LitElement, nothing } from 'lit';
import { html as staticHtml, StaticValue, literal } from 'lit/static-html.js';
import { property, query, state } from 'lit/decorators.js';
import '@material/web/textfield/filled-text-field.js';
import '@material/web/textfield/outlined-text-field.js';
import {
  TextFieldType,
  UnsupportedTextFieldType,
} from '@material/web/textfield/internal/text-field.js';
import { AriaForwardMixin } from '@digital-realty/ix-shared-fns/aria-forward-mixin.js';
import { spread } from '@open-wc/lit-helpers';
import '@digital-realty/ix-icon/ix-icon.js';
import '@digital-realty/ix-phone-input/ix-phone-input.js';

const IxTextboxBase = AriaForwardMixin(LitElement);

export class IxTextbox extends IxTextboxBase {
  @query('.textfield')
  readonly component!: HTMLInputElement;

  @property({ type: Boolean, reflect: true }) filled = false;

  @property({ type: Boolean, reflect: true }) disabled = false;

  /**
   * Gets or sets whether or not the text field is in a visually invalid state.
   *
   * Calling `reportValidity()` will automatically update `error`.
   */
  @property({ type: Boolean, reflect: true }) error = false;

  /**
   * The error message that replaces supporting text when `error` is true. If
   * `errorText` is an empty string, then the supporting text will continue to
   * show.
   *
   * Calling `reportValidity()` will automatically update `errorText` to the
   * native `validationMessage`.
   */
  @property({ attribute: 'error-text' }) errorText = '';

  @property() label = '';

  @property({ type: Boolean, reflect: true }) required = false;

  /**
   * The current value of the text field. It is always a string.
   */
  @property() value = '';

  /**
   * The name of the text field. It is always a string.
   */
  @property() name = '';

  /**
   * An optional prefix to display before the input value.
   */
  @property({ attribute: 'prefix-text' }) prefixText = '';

  /**
   * An optional suffix to display after the input value.
   */
  @property({ attribute: 'suffix-text' }) suffixText = '';

  /**
   * Name of icon. It is always a string.
   */
  @property({ attribute: 'leading-icon' })
  leadingIcon = '';

  /**
   * Name of icon. It is always a string.
   */
  @property({ attribute: 'trailing-icon' })
  trailingIcon = '';

  /**
   * Enables navigation icons for search input types.
   */
  @property({ type: Boolean }) searchEnabled = false;

  /**
   * The number of search results found.
   */
  @property({ type: Number }) searchResultCount = 0;

  /**
   * The index of the selected search result. This property is 1-based (i.e. begins with 1, not zero-based).
   */
  @property({ type: Number }) searchResultIndex = 0;

  /**
   * Conveys additional information below the text field, such as how it should
   * be used.
   */
  @property({ attribute: 'supporting-text' }) supportingText = '';

  /**
   * Override the input text CSS `direction`. Useful for RTL languages that use
   * LTR notation for fractions.
   */
  @property({ attribute: 'text-direction' }) textDirection = '';

  /**
   * The number of rows to display for a `type="textarea"` text field.
   * Defaults to 2.
   */
  @property({ type: Number }) rows = 2;

  // <input> properties
  @property({ reflect: true }) override inputMode = '';

  /**
   * Defines the greatest value in the range of permitted values.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#max
   */
  @property() max = '';

  /**
   * The maximum number of characters a user can enter into the text field. Set
   * to -1 for none.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#maxlength
   */
  @property({ type: Number }) maxLength = -1;

  /**
   * Defines the most negative value in the range of permitted values.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#min
   */
  @property() min = '';

  /**
   * The minimum number of characters a user can enter into the text field. Set
   * to -1 for none.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#minlength
   */
  @property({ type: Number }) minLength = -1;

  /**
   * A regular expression that the text field's value must match to pass
   * constraint validation.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#pattern
   */
  @property() pattern = '';

  @property({ reflect: true }) placeholder = '';

  /**
   * Indicates whether or not a user should be able to edit the text field's
   * value.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#readonly
   */
  @property({ type: Boolean, reflect: true }) readOnly = false;

  /**
   * Indicates that input accepts multiple email addresses.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#multiple
   */
  @property({ type: Boolean, reflect: true }) multiple = false;

  /**
   * Returns or sets the element's step attribute, which works with min and max
   * to limit the increments at which a numeric or date-time value can be set.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#step
   */
  @property() step = '';

  /**
   * The `<input>` type to use, defaults to "text". The type greatly changes how
   * the text field behaves.
   *
   * Text fields support a limited number of `<input>` types:
   *
   * - text
   * - textarea
   * - email
   * - number
   * - password
   * - search
   * - tel
   * - url
   *
   * See
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input#input_types
   * for more details on each input type.
   */
  @property({ reflect: true })
  type: TextFieldType | UnsupportedTextFieldType = 'text';

  /**
   * Describes what, if any, type of autocomplete functionality the input
   * should provide.
   *
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @property({ reflect: true }) autocomplete = '';

  /**
   * True if the input has text.
   */
  @state() hasText: boolean = false;

  textarea: HTMLElement | null | undefined;

  handleKeydownHandler: any = undefined;

  connectedCallback() {
    super.connectedCallback();

    if (this.type === 'textarea') {
      this.addEventListener('input', this.resizeTextArea);
      this.addEventListener('keydown', this.resizeTextArea);
      window.addEventListener('load', this.resizeTextArea);
    }
  }

  async firstUpdated() {
    if (this.type === 'textarea') {
      if (this.component instanceof LitElement) {
        await this.component.updateComplete;
      }
      await this.updateComplete;

      this.textarea = this.component?.shadowRoot?.querySelector('textarea');
      this.resizeTextArea();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.handleKeydownHandler) {
      this.removeEventListener('input', this.resizeTextArea);
      this.removeEventListener('keydown', this.resizeTextArea);
      window.removeEventListener('load', this.resizeTextArea);
    }
  }

  resizeTextArea = () => {
    if (this.textarea) {
      this.textarea.style.height = 'auto';
      if (this.textarea.scrollHeight > 0) {
        this.textarea.style.height = `${this.textarea.scrollHeight}px`;
      }
    }
  };

  focus() {
    this.component.focus();
  }

  protected createRenderRoot() {
    return this;
  }

  renderStyles() {
    return html`
      <style>
        [error] {
          --md-outlined-field-outline-width: 2px;
        }
        ix-textbox {
          &.comment {
            --md-outlined-text-field-container-shape: 32px;
            --md-outlined-text-field-outline-color: rgba(9, 34, 65, 0.12);
            --md-outlined-text-field-outline-width: 1px;
            --md-outlined-text-field-hover-outline-color: rgba(9, 34, 65, 0.12);
            --md-outlined-text-field-hover-outline-width: 1px;
            --md-outlined-text-field-focus-outline-color: rgba(9, 34, 65, 0.12);
            --md-outlined-text-field-focus-outline-width: 1px;
            --md-outlined-text-field-input-text-placeholder-color: rgba(
              9,
              34,
              65,
              0.42
            );
            --md-filled-text-field-focus-active-indicator-color: rgba(
              9,
              34,
              65,
              0.12
            );
            --md-outlined-text-field-input-text-color: rgb(9, 34, 65);
            --md-outlined-field-leading-space: 8px;
          }
          [type='textarea'] {
            resize: none;
          }
        }
      </style>
    `;
  }

  /**
   * Handles search navigation icon clicks and emits search-navigation event.
   */
  private onSearchNavigation(e: CustomEvent, direction: string) {
    this.dispatchEvent(
      new CustomEvent('search-navigation', {
        detail: { direction },
        bubbles: true,
        composed: true,
      })
    );
  }

  /**
   * Handles input event and sets hasText state when an input event is received.
   */
  private onInput = (e: Event) => {
    const input = e.target as HTMLInputElement;
    this.hasText = input.value.length > 0;

    this.dispatchEvent(
      new CustomEvent('search-text-change', {
        detail: { searchText: input.value },
      })
    );
  };

  /**
   * Clears the input field and resets hasText state.
   */
  private clearInput = () => {
    this.value = '';
    this.hasText = false;
    this.dispatchEvent(new Event('input', { bubbles: true, composed: true }));
  };

  /**
   * Sets the search result text based on the current search state.
   * @returns The search result text.
   */
  private setSearchResultText(): string {
    if (this.searchResultCount === 0) {
      return `${this.searchResultCount} results`;
    }

    if (this.searchResultIndex === 0) {
      return `${this.searchResultCount} results`;
    }

    return `${this.searchResultIndex}/${this.searchResultCount} results`;
  }

  protected override render() {
    if (this.type === 'tel') {
      return html`<ix-phone-input
        class="textfield"
        style="display: block;"
        ?disabled=${this.disabled}
        ?required=${this.required}
        ?error=${this.error}
        error-text=${this.errorText}
        supporting-text=${this.supportingText}
        type=${this.type}
        label=${this.label}
        name=${this.name}
        .value=${this.value ?? ''}
        ${spread(this.ariaAttributes)}
      ></ix-phone-input>`;
    }

    const tag: StaticValue = this.filled
      ? literal`md-filled-text-field`
      : literal`md-outlined-text-field`;

    return staticHtml`
        ${this.renderStyles()}
        <${tag}
          class="textfield"
          style="display: block;"
          ?disabled=${this.disabled}
          ?required=${this.required}
          ?error=${this.error}
          ?multipule=${this.multiple}
          ?readOnly=${this.readOnly}
          ?has-leading-icon=${this.leadingIcon.length > 0}
          ?has-trailing-icon=${this.trailingIcon.length > 0}
          step=${this.step || nothing}
          autocomplete=${this.autocomplete || nothing}
          error-text=${this.errorText}
          placeholder=${this.placeholder}
          text-direction=${this.textDirection}
          rows=${this.rows}
          max=${this.max}
          maxLength=${this.maxLength}
          min=${this.min}
          minLength=${this.minLength}
          pattern=${this.pattern}
          prefix-text=${this.prefixText}
          suffix-text=${this.suffixText}
          supporting-text=${this.supportingText}
          inputMode=${this.inputMode}
          type=${this.type}
          label=${this.label}
          name=${this.name}
          .value=${this.value ?? ''}
          @input=${this.onInput}
          ${spread(this.ariaAttributes)}
        >
          ${
            this.leadingIcon
              ? html`<ix-icon slot="leading-icon">${this.leadingIcon}</ix-icon>`
              : nothing
          }
          ${
            this.searchEnabled && this.hasText && this.value.length > 1
              ? html`<div
                  style="font-size: 14px; align-items: center;"
                  slot="trailing-icon"
                >
                  ${this.setSearchResultText()}
                </div>`
              : nothing
          }
          ${
            this.searchEnabled && this.searchResultCount > 0
              ? html`<ix-icon
                    style="padding: 0px 3px; align-items: center; cursor: pointer;"
                    slot="trailing-icon"
                    @click=${(ev: CustomEvent) =>
                      this.onSearchNavigation(ev, 'previous')}
                    >chevron_left</ix-icon
                  >

                  <ix-icon
                    slot="trailing-icon"
                    style="padding: 0px 3px; align-items: center; cursor: pointer;"
                    @click=${(ev: CustomEvent) =>
                      this.onSearchNavigation(ev, 'next')}
                    >chevron_right</ix-icon
                  >`
              : nothing
          }
          ${
            this.hasText
              ? html`<ix-icon
                  style="padding: 0px 3px; align-items: center; cursor: pointer;"
                  slot="trailing-icon"
                  @click=${this.clearInput}
                  >close</ix-icon
                >`
              : nothing
          }
          ${
            this.trailingIcon
              ? html`<ix-icon slot="trailing-icon"
                  >${this.trailingIcon}</ix-icon
                >`
              : nothing
          }
        </${tag}>
      `;
  }
}
